// src/components/Signup.js
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Cookies from "js-cookie";
import axios from "axios";
import {
  FaUser,
  FaPhoneAlt,
  FaKey,
  FaEnvelope,
  FaBusinessTime,
  FaEyeSlash,
  FaEye,
  FaBuilding,
  FaMapMarkedAlt
} from "react-icons/fa";
import image from "../image/login-model.png";
import bowser from "bowser";
import FingerprintJS from "@fingerprintjs/fingerprintjs"; // For browser fingerprinting
import { FaLocationPin } from "react-icons/fa6";
import LeftPanel from "./LeftPanel";
import apiClient from "../common/apiClient";
import { postLoginDestination, saveLoginSession } from "../common/authSession";
import ReCAPTCHA from "react-google-recaptcha";
import ProBrokerFooter from "./ProBrokerFooter";
import { toast } from "react-toastify";

const CITY_LIST_API = "/ijscui/probroker-admin/ceknvuhbd/PremiumPlan/protected/city/all";

// City is a required field, so the dropdown must never render empty. If the city
// API fails or returns nothing we keep this list rather than blocking signup.
const SIGNUP_CITY_FALLBACK = [
  "Ahmedabad West",
  "Ahmedabad East",
  "Gandhinagar",
  "Mumbai",
  "Pune",
];

// The endpoint's payload nesting isn't fixed across this API, so accept the
// shapes it can arrive in and read the city name from whichever key is present.
const extractCityNames = (payload) => {
  const list =
    [payload?.data?.docs, payload?.data, payload?.docs, payload].find((c) => Array.isArray(c)) || [];

  return list
    .map((item) =>
      typeof item === "string" ? item : item?.cityName || item?.name || item?.city || ""
    )
    .map((name) => String(name).trim())
    .filter(Boolean);
};

// Same tolerant read as above, but keeps each city's state so the State dropdown
// can be built from the city API response and the City dropdown filtered by it.
const extractCities = (payload) => {
  const list =
    [payload?.data?.docs, payload?.data, payload?.docs, payload].find((c) => Array.isArray(c)) || [];

  return list
    .map((item) => {
      if (typeof item === "string") return { name: item.trim(), state: "" };
      const name = item?.cityName || item?.name || item?.city || "";
      const state = item?.State || item?.state?.name || item?.stateName || item?.state || "";
      return { name: String(name).trim(), state: String(state).trim() };
    })
    .filter((c) => c.name);
};

const Signup = () => {
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [noOfStaff, setNoOfStaff] = useState("")
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false); // Loading state
  const [acceptedTerms, setAcceptedTerms] = useState(true); // State for terms and conditions
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false); // State to toggle password visibility
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [userType, setUserType] = useState("company");
  const [cityOptions, setCityOptions] = useState(SIGNUP_CITY_FALLBACK);
  // States come only from the city API response (each city carries its State).
  const [stateOptions, setStateOptions] = useState([]);
  const [citiesWithState, setCitiesWithState] = useState([]);

  // OTP verification states
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const otpInputRefs = useRef([]);

  // Resend OTP countdown timer
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Populate the city dropdown from the admin city list.
  useEffect(() => {
    let cancelled = false;

    apiClient("get", CITY_LIST_API).then((res) => {
      if (cancelled || !res.success) return;
      const names = extractCityNames(res.data);
      if (names.length) setCityOptions(names);

      const cities = extractCities(res.data);
      const states = [...new Set(cities.map((c) => c.state).filter(Boolean))].sort();
      setStateOptions(states);
      setCitiesWithState(cities);
    });

    return () => { cancelled = true; };
  }, []);

  // When the API gives a state per city, only show the cities of the chosen state.
  const visibleCityOptions =
    state && citiesWithState.length
      ? citiesWithState.filter((c) => c.state === state).map((c) => c.name)
      : cityOptions;

  const handleStateChange = (value) => {
    setState(value);
    setCity("");
  };

  // Function to gather device details
  const getDeviceDetails = () => {
    const browser = bowser.getParser(window.navigator.userAgent);
    const info = browser.getResult();

    return {
      browser: info.browser.name,
      browserVersion: info.browser.version,
      os: info.os.name,
      osVersion: info.os.version,
      platform: info.platform.type,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      userAgent: navigator.userAgent,
    };
  };
  const togglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState); // Toggle the password visibility state
  };
  // Function to get browser fingerprint (unique ID for the browser)
  const getFingerprint = async () => {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    return result.visitorId; // Unique ID for browser
  };

  // Function to get the user's IP address
  const getIPAddress = async () => {
    try {
      const response = await axios.get("https://api.ipify.org?format=json");
      return response.data.ip; // Returns the IP address of the user
    } catch (error) {
      // console.error("Unable to fetch IP address", error);
      return null;
    }
  };
  const handleCaptcha = (token) => {
    setRecaptchaToken(token);
  };

  // Step 1: Send OTP on Sign Up click
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Validate form fields
    if (!name || !number || !email || !address || !companyName || !state || !city) {
      setError("All fields are required.");
      setLoading(false);
      return;
    }

    if (!acceptedTerms) {
      setError("Please accept the terms and conditions to proceed.");
      setLoading(false);
      return;
    }

    // Validate contact number format
    const contactPattern = /^[0-9]{10}$/;
    if (!contactPattern.test(number)) {
      setError("Invalid contact number format.");
      setLoading(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      setLoading(false);
      return;
    }

    // Password field is commented out, so its length check is disabled too.
    // if (password.length < 6) {
    //   setError("Password must be at least 6 characters long.");
    //   setLoading(false);
    //   return;
    // }

    try {
      // Call send-otp API instead of the registration API
      const response = await apiClient(
        "post",
        "/cjidnvij/ceksfbuebijn/user/register/send-otp",
        {
          number,
          companyName,
          name,
          email,
          noOfStaff,
          address,
          state,
          city,
        }
      );

      if (response.success) {
        toast.success("OTP sent successfully to your phone number!");
        setShowOtpScreen(true);
        setOtp(["", "", "", "", "", ""]);
        setOtpError("");
        setResendTimer(60); // Start 60s countdown for resend
        // Auto-focus first OTP input
        setTimeout(() => {
          if (otpInputRefs.current[0]) {
            otpInputRefs.current[0].focus();
          }
        }, 100);
      } else {
        const errorMsg = response.error?.message || response.error || "Failed to send OTP. Please try again.";
        setError(typeof errorMsg === "string" ? errorMsg : "Failed to send OTP. Please try again.");
      }
    } catch (err) {
      setError("Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setOtpError("");

    // Auto-focus next input
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  // Handle OTP paste
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split("");
      setOtp(digits);
      setOtpError("");
      otpInputRefs.current[5]?.focus();
    }
  };

  // Handle OTP backspace
  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    setOtpError("");
    setOtpLoading(true);

    try {
      // Calling this API for Resend OTP and sending the user's number
      const response = await apiClient(
        "post",
        "/cjidnvij/ceksfbuebijn/user/register/send-otp",
        {
          number,
          companyName,
          name,
          email,
          noOfStaff,
          address,
          state,
          city,
        }
      );

      if (response.success) {
        toast.success("OTP resent successfully!");
        setOtp(["", "", "", "", "", ""]);
        setResendTimer(60);
        setTimeout(() => {
          if (otpInputRefs.current[0]) {
            otpInputRefs.current[0].focus();
          }
        }, 100);
      } else {
        const errorMsg = response.error?.message || response.error || "Failed to resend OTP.";
        setOtpError(typeof errorMsg === "string" ? errorMsg : "Failed to resend OTP.");
      }
    } catch (err) {
      setOtpError("Failed to resend OTP. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  // Step 2: Verify OTP and then register
  const handleVerifyOtp = async () => {
    const otpString = otp.join("");

    if (otpString.length !== 6) {
      setOtpError("Please enter the complete 6-digit OTP.");
      return;
    }

    setOtpLoading(true);
    setOtpError("");

    try {
      // Call verify-otp API
      const verifyResponse = await apiClient(
        "post",
        "/cjidnvij/ceksfbuebijn/user/register/verify-otp",
        {
          number,
          otp: otpString,
        }
      );

      if (!verifyResponse.success) {
        const errorMsg = verifyResponse.error?.message || verifyResponse.error || "Invalid OTP. Please try again.";
        setOtpError(typeof errorMsg === "string" ? errorMsg : "Invalid OTP. Please try again.");
        setOtpLoading(false);
        return;
      }

      toast.success("OTP verified successfully!");

      // Step 3: OTP verified — now call the registration API
      const deviceDetails = getDeviceDetails();
      const fingerprint = await getFingerprint();
      const ipAddress = await getIPAddress();

      const registerResponse = await apiClient(
        "post",
        "/cjidnvij/ceksfbuebijn/user/v2/signin/vkjdbfuhe/nkdkjbed",
        {
          userType,
          companyName,
          noOfStaff,
          email,
          address,
          state,
          city,
          name,
          city,
          number,
          password,
          deviceDetails,
          fingerprint,
          ipAddress,
          recaptchaToken,
          // Every new registrant is flagged tool-only: the dashboard shows them
          // just the Verify Listing tool -- no sidebar, no property browsing --
          // until their city has live listings (DashboardLayout gates that).
          toolOnlyAccess: true,
        }
      );

      // Handle API SUCCESS/FAIL
      if (!registerResponse.success) {
        if (registerResponse.error?.message === "Demo plan not found") {
          toast.success("Your Account successfully created");
          navigate("/login");
          return;
        }

        setOtpError(registerResponse.error?.message || "Signup failed.");
        return;
      }

      const res = registerResponse.data; // decrypted or normal response

      if (res.status === "success") {
        toast.success("Your Account successfully created!");

        // The backend logs an OTP-verified signup straight in: `session` has the
        // same { token, user, paymentHistory } as the login response. Save it
        // like a login and skip the login screen. Without a session (older
        // backend, or session creation failed) fall back to logging in.
        const session = res.session;
        if (session?.token && session?.user?._id) {
          await saveLoginSession(session);
          navigate(postLoginDestination());
        } else {
          navigate("/login");
        }
        window.location.reload();
      } else if (res.status === "error" && res.message === "Demo plan not found") {
        toast.success("Your Account successfully created");
        navigate("/login");
      } else {
        setOtpError(res.message || "Unexpected error");
      }

    } catch (err) {
      setOtpError("Unexpected failure. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  // Go back from OTP screen to form
  const handleBackToForm = () => {
    setShowOtpScreen(false);
    setOtp(["", "", "", "", "", ""]);
    setOtpError("");
    setResendTimer(0);
  };

  // Render OTP Verification Screen
  const renderOtpScreen = () => (
    <div className="flex-1 mx-2 lg:mx-20 mb-12 flex items-center justify-center">
      <div className="flex-1 max-w-lg rounded-lg mt-10">
        {/* Back button */}
        <button
          type="button"
          onClick={handleBackToForm}
          className="flex items-center gap-2 text-[#503691] font-medium mb-6 hover:underline"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back to Sign Up
        </button>

        {/* OTP Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-[#503691]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaPhoneAlt className="text-[#503691] text-2xl" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Number</h2>
          <p className="text-gray-500 text-sm">
            We've sent a 6-digit OTP to{" "}
            <span className="font-semibold text-gray-700">+91 {number}</span>
          </p>
        </div>

        {/* OTP Input Boxes */}
        <div className="flex justify-center gap-3 mb-6">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (otpInputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleOtpKeyDown(index, e)}
              onPaste={index === 0 ? handleOtpPaste : undefined}
              className={`w-12 h-14 text-center text-xl font-bold border-2 rounded-lg outline-none transition-all duration-200
                ${digit ? "border-[#503691] bg-[#503691]/5" : "border-gray-300"}
                focus:border-[#503691] focus:ring-2 focus:ring-[#503691]/20`}
              autoComplete="one-time-code"
            />
          ))}
        </div>

        {/* OTP Error */}
        {otpError && (
          <div className="text-red-500 bg-red-100 border border-red-500 rounded-md p-2 text-center mb-4 text-sm">
            {otpError}
          </div>
        )}

        {/* Verify Button */}
        <button
          type="button"
          onClick={handleVerifyOtp}
          disabled={otpLoading || otp.join("").length !== 6}
          className="w-full h-12 bg-[#503691] text-white rounded-full font-semibold hover:bg-[#5b54e6] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2"
        >
          {otpLoading ? (
            <>
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Verifying...
            </>
          ) : (
            "Verify & Create Account"
          )}
        </button>

        {/* Resend OTP */}
        <div className="text-center mt-6">
          <p className="text-gray-500 text-sm">
            Didn't receive the OTP?{" "}
            {resendTimer > 0 ? (
              <span className="text-gray-400">
                Resend in <span className="font-semibold text-[#503691]">{resendTimer}s</span>
              </span>
            ) : (
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={otpLoading}
                className="text-[#503691] font-semibold hover:underline disabled:opacity-50"
              >
                Resend OTP
              </button>
            )}
          </p>
        </div>

        {/* Login link */}
        <div className="text-center mt-6">
          <p>
            Already a member?{" "}
            <Link to="/login" className="text-[#503691] font-semibold">
              Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className={`flex w-full overflow-hidden ${showOtpScreen ? "h-screen" : "min-h-screen"}`}>
        {/* Left panel with animations and graphics */}
        <LeftPanel className="" />

        {showOtpScreen ? (
          renderOtpScreen()
        ) : (
          <form onSubmit={handleSubmit} className="mx-2 lg:mx-20 mb-12 ">

            {/* <p className="text-4xl text-gray-800 mb-1 mt-11">Welcome to <span className="text-5xl font-bold text-[#503691] mb-4">PRObroker</span> </p> */}
            <p className="text-3xl font-bold mb-4">Create Account</p>
            <div className="mb-4">
              <label className="block mb-2 font-medium">Select User Type</label>

              <div className="flex gap-6">
                {/* Company Based */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="userType"
                    value="company"
                    checked={userType === "company"}
                    onChange={(e) => setUserType(e.target.value)}
                    className="accent-[#503691]"
                  />
                  Company Account
                </label>
                {/* Single Based */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="userType"
                    value="single"
                    checked={userType === "single"}
                    onChange={(e) => setUserType(e.target.value)}
                    className="accent-[#503691]"
                  />
                  Single
                </label>
              </div>
            </div>
            <div className="relative mb-4">
              <FaBusinessTime className="absolute top-1/2 left-4 transform -translate-y-1/2 text-[#503691]" />
              <input
                type="text"
                placeholder="Enter Company Name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="w-full px-10 py-3 border border-gray-300 rounded-md"
                required
              />
            </div>
            {userType === "company" && <div className="relative mb-4">
              <FaBusinessTime className="absolute top-1/2 left-4 transform -translate-y-1/2 text-[#503691]" />
              <input
                type="text"
                placeholder="Enter Your No. of Staff"
                value={noOfStaff}
                onChange={(e) => setNoOfStaff(e.target.value)}
                className="w-full px-10 py-3 border border-gray-300 rounded-md"
                required
              />
            </div>}

            <div className="relative mb-4">
              <FaUser className="absolute top-1/2 left-4 transform -translate-y-1/2 text-[#503691]" />
              <input
                type="text"
                placeholder="Enter Your Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-10 py-3 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div className="relative mb-4">
              <FaPhoneAlt className="absolute top-1/2 left-4 transform -translate-y-1/2 text-[#503691]" />
              <input
                type="text"
                placeholder="Enter Your Contact Number"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                className="w-full px-10 py-3 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div className="relative mb-4">
              <FaEnvelope className="absolute top-1/2 left-4 transform -translate-y-1/2 text-[#503691]" />
              <input
                type="text"
                placeholder="Enter Your Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-10 py-3 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div className="relative mb-4">
              <FaLocationPin className="absolute top-1/2 left-4 transform -translate-y-1/2 text-[#503691]" />
              <input
                type="text"
                placeholder="Enter Your Address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="w-full px-10 py-3 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div className="relative mb-4">
              <FaMapMarkedAlt className="absolute top-1/2 left-4 transform -translate-y-1/2 text-[#503691]" />

              <select
                value={state}
                onChange={(e) => handleStateChange(e.target.value)}
                className="w-full px-10 py-3 border border-gray-300 rounded-md appearance-none"
                required
              >
                <option value="">Select Your State</option>
                {stateOptions.map((stateName) => (
                  <option key={stateName} value={stateName}>{stateName}</option>
                ))}
              </select>
            </div>
            <div className="relative mb-4">
              <FaLocationPin className="absolute top-1/2 left-4 transform -translate-y-1/2 text-[#503691]" />

              <select
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full px-10 py-3 border border-gray-300 rounded-md appearance-none"
                required
              >
                <option value="">Select Your City</option>
                {visibleCityOptions.map((cityName) => (
                  <option key={cityName} value={cityName}>{cityName}</option>
                ))}
              </select>
            </div>
            {/* Password field commented out - signup is OTP only for now
            <div className="relative mb-4">
              <FaKey className="absolute top-1/2 left-4 transform -translate-y-1/2 text-[#503691]" />
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter Your Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-10 py-3 border border-gray-300 rounded-md"
                required
              />
              <button
                type="button"
                className="absolute top-1/2 right-4 transform -translate-y-1/2 text-[#503691] focus:outline-none"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            */}
            {error && (
              <div className="text-red-500 bg-red-100 border border-red-500 rounded-md p-2 text-center mb-2">
                {error}
              </div>
            )}
            <div className="flex items-center gap-2 py-3">
              <input
                type="checkbox"
                id="terms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="h-6 w-6 rounded border-gray-300 text-[#503691] focus:ring-[#503691] cursor-pointer"
              />
              <label htmlFor="terms" className="lg:text-[15px] text-[13px] text-gray-700 text-center cursor-pointer">
                By clicking on signup button you're accepting our{" "}
                <Link to="/terms-and-condition" className="text-blue-500 hover:underline">
                  terms and conditions
                </Link>{" "}
                and{" "}
                <Link to="/privacy-policy" className="text-blue-500 hover:underline">
                  privacy policy
                </Link>
              </label>
            </div>

            <button
              type="submit"
              className="w-full h-11 bg-[#503691] text-white rounded-full font-semibold hover:bg-[#5b54e6] disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || !acceptedTerms}
            >
              {loading ? "Sending OTP..." : "Sign Up"}
            </button>
            {loading && <div className="mt-2 text-center">Sending OTP...</div>}
            <div className="text-center mt-4  ">
              <p>
                Already a member?{" "}
                <Link to="/login" className="text-[#503691] font-semibold">
                  Login
                </Link>
              </p>
            </div>
          </form>
        )}
      </div>
      {/* <ProBrokerFooter /> */}
    </>

  );
};

export default Signup;
